import React, { useState, useCallback, useMemo } from "react";
import { useTranslation } from "components/translations/useTranslation";
import { MODAL_CONTENT_TAB_INDEX } from "utils/constants";
import OKCancelButtons from "components/modals/OKCancelButtons";
import { formatDate, formatCalendarDate } from "utils/valueFormatter.function";
import { NewCalendar } from "components/common/calendar/Calendar.component";
import { DateRange } from "components/common/calendar/CalendarQuickSelect.component";
import { postponeTasksByUID } from "components/modals/modal.actions";
import { useDispatch } from "react-redux";
import { FormField } from "components/common/FormField.component";
import { Notification, NotificationType } from "components/common/Notification.component";
import { ModalContentProps } from "types/modals";

const getTomorrow = (): Date => {
    const tomorrow = new Date();
    tomorrow.setHours(12, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
};

const getMinDocumentDueDate = (tasks: Array<Record<string, any>>): string | null => {
    return tasks.reduce<string | null>((acc, val) => {
        if (acc === null) return val.documentDueDate;
        return acc < val.documentDueDate ? acc : val.documentDueDate;
    }, null);
};

const getMinTaskDueDate = (tasks: Array<Record<string, any>>): string | null => {
    return tasks.reduce<string | null>((acc, val) => {
        if (!val.dueDate) return acc;
        if (acc === null) return val.dueDate;
        return acc < val.dueDate ? acc : val.dueDate;
    }, null);
};

// Initial task due date: the single task's current due date when it is still in the
// future, otherwise tomorrow. With multiple tasks we always default to tomorrow.
const getInitialDueDate = (tasks: Array<Record<string, any>>): Date => {
    const tomorrow = getTomorrow();
    if (tasks.length === 1 && tasks[0]?.dueDate) {
        const due = new Date(tasks[0].dueDate);
        due.setHours(12, 0, 0, 0);
        if (due.getTime() >= tomorrow.getTime()) {
            return due;
        }
    }
    return tomorrow;
};

interface PostponeModalProps extends ModalContentProps {
    more: {
        tasks: Array<Record<string, any>>;
    };
}

const Postpone = ({ more, closeModal, loopFocus, initialFocusRef }: PostponeModalProps): React.ReactElement => {
    const { translate } = useTranslation();
    const dispatch = useDispatch();

    const tomorrow = useMemo(getTomorrow, []);
    const minDocDueDate = useMemo(() => getMinDocumentDueDate(more.tasks), [more.tasks]);
    const minTaskDueDate = useMemo(() => getMinTaskDueDate(more.tasks), [more.tasks]);
    const [newDueDate, setNewDueDate] = useState<Date>(() => getInitialDueDate(more.tasks));
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const isAfterDocDueDate =
        !!minDocDueDate && newDueDate.getTime() > new Date(minDocDueDate).getTime();

    const handlePostponeError = useCallback(() => {
        setErrorMessage(translate("popUp.postpone.actionError"));
    }, [translate]);

    const handlePostpone = useCallback(() => {
        setErrorMessage(null);
        const tasks = more.tasks ?? [];
        const postponeMessage = translate("popUp.postpone.actionSuccess", tasks.length);
        const newTaskDueDate = formatCalendarDate(newDueDate);
        const comment = `Task postponed to ${newTaskDueDate}`;
        const uids = tasks.map((task) => task.uid);

        // Postpone each selected task individually: POST /tasks/{uid}/postpone
        dispatch(postponeTasksByUID(uids, comment, newTaskDueDate, postponeMessage, closeModal, handlePostponeError));
    }, [more.tasks, translate, newDueDate, dispatch, closeModal, handlePostponeError]);

    return (
        <>
            <div className="ga-modal__content">
                <form>
                    {minDocDueDate && (
                        <FormField
                            id="postponeDocumentDueDate"
                            label={translate("popUp.postpone.documentDueDate")}
                        >
                            <span className="d-inline-block ml-4">{formatDate(minDocDueDate, null)}</span>
                            {isAfterDocDueDate && (
                                <div className="mt-8">
                                    <Notification
                                        type={NotificationType.WARNING}
                                        description={translate("popUp.postpone.afterDocDueDate", formatDate(minDocDueDate, null))}
                                    />
                                </div>
                            )}
                        </FormField>
                    )}

                    {minTaskDueDate && (
                        <FormField
                            id="postponeCurrentTaskDueDate"
                            label={translate("popUp.postpone.taskDueDate")}
                        >
                            <span className="d-inline-block ml-4">{formatDate(minTaskDueDate, null)}</span>
                        </FormField>
                    )}
                    <FormField
                        id="postponeTaskDueDate"
                        label={translate("popUp.postpone.newTaskDueDate")}
                    >
                        <NewCalendar
                            id="postponeTaskDueDatePicker"
                            singlePanel
                            translate={translate}
                            minDate={tomorrow}
                            initialStartDate={newDueDate}
                            initialEndDate={newDueDate}
                            onChange={(range: DateRange) => {
                                if (range.startDate) {
                                    setNewDueDate(range.startDate);
                                }
                            }}
                        />
                    </FormField>
                    {errorMessage && (
                        <div className="mb-16">
                            <Notification
                                type={NotificationType.ERROR}
                                description={errorMessage}
                            />
                        </div>
                    )}
                </form>
            </div>
            <OKCancelButtons
                tabIndex={MODAL_CONTENT_TAB_INDEX + 2}
                okRef={initialFocusRef as React.Ref<HTMLButtonElement>}
                okEnabled={!!newDueDate}
                okCallback={handlePostpone}
                okText={translate("popUp.postpone.postponeTask")}
                cancelEnabled={true}
                cancelCallback={closeModal}
                cancelText={translate("popUp.close")}
                loopFocus={loopFocus}
            />
        </>
    );
};

export default Postpone;